/* ==========================================================================
   Snipes — Collections
   Grid layout: "View more" expands the hidden rows in place.
   Carousel layout: manual scroll, drag-to-scroll and a progress line.
   ========================================================================== */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
     GRID: reveal extra rows on demand
     ------------------------------------------------------------------ */
  function initGrid(root) {
    var grid = root.querySelector('[data-snp-cc-grid]');
    var wrap = root.querySelector('[data-snp-cc-more-wrap]');
    var button = root.querySelector('[data-snp-cc-more]');
    if (!grid || !button || !wrap) return;

    var label = button.querySelector('[data-snp-cc-more-text]');
    var items = Array.prototype.slice.call(grid.querySelectorAll('.snp-cc__item'));
    var rows = parseInt(root.dataset.rows, 10) || 1;
    var open = false;

    // How many columns the grid is actually rendering right now
    function columnCount() {
      var template = window.getComputedStyle(grid).gridTemplateColumns;
      if (!template || template === 'none') return 1;
      return template.split(' ').filter(function (v) { return v.trim() !== ''; }).length;
    }

    // Height of the first `rows` rows, measured from the real layout
    function collapsedHeight(visibleCount) {
      var last = items[visibleCount - 1];
      if (!last) return null;
      return last.offsetTop + last.offsetHeight;
    }

    function markClipped(visibleCount) {
      items.forEach(function (item, i) {
        item.classList.toggle('is-clipped', i >= visibleCount);
      });
    }

    function apply(animate) {
      var cols = columnCount();
      var visibleCount = cols * rows;

      // Everything already fits — no button needed
      if (items.length <= visibleCount) {
        wrap.hidden = true;
        grid.style.maxHeight = '';
        grid.classList.add('is-open');
        markClipped(items.length);
        return;
      }

      wrap.hidden = false;
      markClipped(visibleCount);

      if (open) {
        grid.classList.add('is-open');
        if (animate) {
          grid.style.maxHeight = grid.scrollHeight + 'px';
          // Once expanded, drop the cap so later reflows aren't clipped
          window.setTimeout(function () {
            if (open) grid.style.maxHeight = 'none';
          }, 600);
        } else {
          grid.style.maxHeight = 'none';
        }
      } else {
        var h = collapsedHeight(visibleCount);
        if (h == null) return;

        if (animate) {
          // Give the transition a concrete starting height
          grid.style.maxHeight = grid.scrollHeight + 'px';
          void grid.offsetHeight; // force reflow
        }
        grid.style.maxHeight = h + 'px';
        grid.classList.remove('is-open');
      }
    }

    button.addEventListener('click', function () {
      open = !open;
      button.setAttribute('aria-expanded', open ? 'true' : 'false');
      if (label) {
        label.textContent = open
          ? (button.dataset.textLess || 'View less')
          : (button.dataset.textMore || 'View more');
      }
      apply(true);

      // Collapsing from far down the page can leave the viewer stranded
      if (!open) {
        var top = root.getBoundingClientRect().top;
        if (top < 0) root.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });

    var resizeTimer = null;
    window.addEventListener('resize', function () {
      window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(function () { apply(false); }, 150);
    });

    // Images settling changes row heights, so re-measure
    grid.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) {
        img.addEventListener('load', function () { apply(false); }, { once: true });
      }
    });

    apply(false);
  }

  /* ------------------------------------------------------------------
     CAROUSEL: progress line + drag to scroll
     ------------------------------------------------------------------ */
  function initCarousel(root) {
    var track = root.querySelector('[data-snp-cc-track]');
    if (!track) return;

    var progress = root.querySelector('[data-snp-cc-progress]');
    var bar = root.querySelector('[data-snp-cc-progress-bar]');

    function updateProgress() {
      if (!progress || !bar) return;

      var visible = track.clientWidth;
      var total = track.scrollWidth;
      var scrollable = total - visible;

      if (scrollable <= 1) {
        progress.classList.add('is-hidden');
        return;
      }
      progress.classList.remove('is-hidden');

      var widthPct = (visible / total) * 100;
      var maxTravel = 100 - widthPct;
      var travelPct = (track.scrollLeft / scrollable) * maxTravel;

      bar.style.width = widthPct + '%';
      bar.style.transform = 'translateX(' + (travelPct / widthPct) * 100 + '%)';

      if (!progress.classList.contains('is-ready')) {
        window.requestAnimationFrame(function () {
          progress.classList.add('is-ready');
        });
      }
    }

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () {
        updateProgress();
        ticking = false;
      });
    }, { passive: true });

    var down = false;
    var startX = 0;
    var startScroll = 0;
    var moved = 0;

    track.addEventListener('pointerdown', function (event) {
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      down = true;
      moved = 0;
      startX = event.clientX;
      startScroll = track.scrollLeft;
      track.classList.add('is-dragging');
    });

    track.addEventListener('pointermove', function (event) {
      if (!down) return;
      var delta = event.clientX - startX;
      moved = Math.abs(delta);
      if (moved > 4 && track.setPointerCapture) {
        track.setPointerCapture(event.pointerId);
      }
      track.scrollLeft = startScroll - delta;
    });

    function endDrag(event) {
      if (!down) return;
      down = false;
      track.classList.remove('is-dragging');
      if (
        event && event.pointerId != null &&
        track.hasPointerCapture && track.hasPointerCapture(event.pointerId)
      ) {
        track.releasePointerCapture(event.pointerId);
      }
    }

    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    track.addEventListener('pointerleave', endDrag);

    track.addEventListener('click', function (event) {
      if (moved > 6) {
        event.preventDefault();
        event.stopPropagation();
      }
    }, true);

    window.addEventListener('resize', function () {
      window.requestAnimationFrame(updateProgress);
    });

    if ('ResizeObserver' in window) {
      new ResizeObserver(function () {
        window.requestAnimationFrame(updateProgress);
      }).observe(track);
    }

    track.querySelectorAll('img').forEach(function (img) {
      if (!img.complete) {
        img.addEventListener('load', updateProgress, { once: true });
      }
    });

    updateProgress();
  }

  /* ------------------------------------------------------------------ */

  function initSection(root) {
    if (!root || root.dataset.snpCcReady === 'true') return;
    root.dataset.snpCcReady = 'true';

    if (root.dataset.layout === 'grid') {
      initGrid(root);
    } else {
      initCarousel(root);
    }
  }

  function initAll(scope) {
    (scope || document).querySelectorAll('[data-snp-cc]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { initAll(); });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });
})();
